import {
  GOOGLE_ADS_CONVERSION_SEND_TO,
  MARKETING_EVENTS,
  type MarketingEventName,
} from "../_constants/marketing";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const firedThisPage = new Set<string>();

function storageKey(event: MarketingEventName, uid: string) {
  return `mm.marketing.${event}.${uid}`;
}

function alreadyFired(event: MarketingEventName, uid: string): boolean {
  const key = storageKey(event, uid);
  if (firedThisPage.has(key)) return true;
  try {
    if (window.localStorage.getItem(key) === "1") {
      firedThisPage.add(key);
      return true;
    }
  } catch {
    // Private mode can throw. In-memory still prevents remount doubles.
  }
  return false;
}

function markFired(event: MarketingEventName, uid: string) {
  const key = storageKey(event, uid);
  firedThisPage.add(key);
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    // Ignore quota / private-mode failures. Memory still covers this tab.
  }
}

// Cap on how long a redirect waits for Google. An ad blocker leaves the gtag
// stub in place but never calls back.
const SEND_TIMEOUT_MS = 1000;
const pendingSends = new Set<Promise<void>>();

function gtagEventSent(name: string, params: Record<string, unknown>) {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, SEND_TIMEOUT_MS);
    const done = () => {
      window.clearTimeout(timer);
      resolve();
    };
    try {
      window.gtag("event", name, { ...params, event_callback: done });
    } catch {
      // Extensions can replace gtag with something that throws.
      done();
    }
  });
}

function sendGtagEvent(
  event: MarketingEventName,
  params: Record<string, unknown>
) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  const sends = [gtagEventSent(event, params)];
  const sendTo = GOOGLE_ADS_CONVERSION_SEND_TO[event];
  if (sendTo) {
    sends.push(gtagEventSent("conversion", { ...params, send_to: sendTo }));
  }

  const sent = Promise.allSettled(sends).then(() => undefined);
  pendingSends.add(sent);
  void sent.then(() => pendingSends.delete(sent));
}

// A hard navigation can cut off a conversion that is still sending, and it is
// already marked fired so it never retries. Await this before leaving the page.
// Never rejects and never waits longer than SEND_TIMEOUT_MS.
export async function flushMarketingEvents() {
  if (!pendingSends.size) return;
  try {
    await Promise.race([
      Promise.allSettled(pendingSends),
      new Promise((resolve) => window.setTimeout(resolve, SEND_TIMEOUT_MS)),
    ]);
  } catch {
    // Tracking must never block a redirect.
  }
}

// Fire a named conversion at most once per uid per browser. Safe to call from
// login, signup confirm, and the layout tracker: remount and refresh no-op.
// transaction_id is always uid:event so Google Ads dedupes across browsers.
// Never throws: callers sit on the signup path and in a layout effect.
export function trackMarketingEventOnce(
  event: MarketingEventName,
  uid: string,
  params: Record<string, unknown> = {}
): boolean {
  if (!uid || typeof window === "undefined") return false;
  try {
    if (alreadyFired(event, uid)) return false;

    markFired(event, uid);
    sendGtagEvent(event, {
      ...params,
      transaction_id: `${uid}:${event}`,
    });
    return true;
  } catch {
    return false;
  }
}

export function trackAccountCreated(uid: string) {
  return trackMarketingEventOnce(MARKETING_EVENTS.accountCreated, uid);
}

export function trackStripeVerified(
  uid: string,
  params: Record<string, unknown> = {}
) {
  return trackMarketingEventOnce(MARKETING_EVENTS.stripeVerified, uid, params);
}
